import { MouseEvent, useEffect, useState } from "react"

import { ReactComponent as IconSettings } from "@common_assets/svg/settings.svg"
import {
  Box,
  Fab,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { useMutationEditClass } from "api/reactQuery/mutations/classes"
import {
  useMutationClearAllApplications,
  useMutationDeleteJobs,
  useMutationPostCreateJobs,
} from "api/reactQuery/mutations/jobs"
import { useQueryClassesById } from "api/reactQuery/queries/classes"
import { useQueryExportClassJobsTask } from "api/reactQuery/queries/export"
import { ExportType } from "api/reactQuery/queries/export.types"
import { useQueryGetJobs } from "api/reactQuery/queries/jobs"
import { useQueryShortClassList } from "api/reactQuery/queries/shortClassList"
import { IShortClassStudent } from "api/reactQuery/queries/shortClassList.types"
import AddJobMenu from "components/common/addJobMenu/AddJobMenu"
import Dialog from "components/common/dialog/dialog"
import DialogUpgradeAccount from "components/common/dialogUpgradeAccount/DialogUpgradeAccount"
import { ExportPendingBanner } from "components/common/exportMenu"
import { PopOverStyle } from "components/common/poprow"
import JobsSkeleton from "components/common/skeleton/jobs"
import { useDialog } from "hooks/dialog"
import { useExportGuard, useExportMenu, useExportTasks } from "hooks/exportReports"
import { useCustomPayment } from "hooks/payment"
import { usePopover } from "hooks/popover"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppSelector } from "hooks/store"
import { useSuggestedJobs } from "hooks/suggestedJobs"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useParams } from "react-router"
import { selectUser } from "store/auth/auth.selectors"
import { getErrorMessageFromTab } from "utils/api"
import { schoolPlanToExportData } from "utils/export"
import { renderMultipleComponents } from "utils/jobs/jobs"

import StudentSkeleton from "../../components/common/skeleton/studentProfile"
import AddJob from "./addJob"
import AddSuggestedJobs from "./addSuggestedJobs"
import NoStudent from "./class/NoStudent"
import StudentBoard from "./class/StudentBoard"
import DialogClearApplication from "./dialog"
import { IAutopayProps } from "./Jobs.types"
import NoJobs from "./jobsBoard/NoJobs"
import JobsOffer from "./jobsOffer"
import { Content } from "./optionsJobPopover"

export interface IStudentsToChecked {
  id: number
  checked: boolean
}
export interface ICheckedPerson {
  id: number
  first_name: string
  last_name: string
}

const Jobs = () => {
  const params = useParams()
  const theme = useTheme()
  const { showSnackbar } = useCustomSnackbar()
  const userInfo = useAppSelector(selectUser)
  const isTablet = useMediaQuery(theme.breakpoints.down("desktop"))
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"))
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [checkedPerson, setCheckedPerson] = useState<ICheckedPerson[] | []>([])
  const [checked, setChecked] = useState(false)
  const [studentsToChecked, setStudentsToChecked] = useState<
    IStudentsToChecked[] | []
  >([])
  const [richEditorResponsibilities, setRichEditorResponsibilities] =
    useState("")
  const [richEditorQualifications, setRichEditorQualifications] = useState("")

  const [autopay, setAutopay] = useState<IAutopayProps>({
    isActive: false,
    value: "",
  })

  const { handleClick, handleClose, popoverAnchorEl } =
    usePopover<HTMLButtonElement>()

  const {
    checkedSuggestedJobs,
    setCheckedSuggestedJobs,
    errorSuggested,
    isDialogOpenSuggested,
    handleOpenDialogSuggested,
    isDialogCloseSuggestedJobs,
    handleOpenDialogCloseSuggestedJobs,
    handleCloseDialogCloseSuggestedJobs,
    handleSuggestedJobs,
    handleCreateSuggestedJobs,
    handleCloseWithConfirmation,
  } = useSuggestedJobs()

  const { data: getJobs, isLoading: loadingGetJobs } = useQueryGetJobs({
    classId: Number(params.classId),
  })

  const { data: classOptions, isLoading: isLoadingClassOptions } =
    useQueryClassesById({
      id: Number(params.classId),
    })

  const {
    data: getStudentClass,
    isLoading: isLoadingStudentClass,
    isRefetching,
  } = useQueryShortClassList({
    classId: Number(params.classId),
    limit: 0,
    options: {
      cacheTime: 0,
      enabled: true,
    },
  })
  const { mutate: patchOpenJob } = useMutationEditClass({
    options: {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["classes"])
        if (data?.data.is_open_for_job_applications) {
          showSnackbar({
            title: t("jobs.jobBoardOpen"),
          })
        } else {
          showSnackbar({
            title: t("jobs.jobBoardClosed"),
          })
        }
      },
      onError: (err) => {
        showSnackbar({
          title: getErrorMessageFromTab(err),
          variant: "error",
        })
      },
    },
  })

  const { payIt, isLoadingPayments } = useCustomPayment()

  const [isExportData, setIsExportData] = useState(false)

  const {
    successTasks,
    pendingTasks,
    setExportReportIdToDownload,
    handleExportSuccess,
    refreshTasks,
  } = useExportTasks({
    exportType: ExportType.CLASS_JOBS,
    classId: Number(params.classId),
  })

  const { isFetching, refetch: refetchExportData } =
    useQueryExportClassJobsTask({
      classId: Number(params.classId),
      options: {
        enabled: isExportData,
        onSuccess: (response) => {
          setIsExportData(false)
          handleExportSuccess(response, Number(params.classId))
          showSnackbar({
            title: t("export.fileIsCurrentlyBeingGenerated"),
          })
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  const {
    isOpen: isOpenExport,
    handleOpen: handleOpenExport,
    handleClose: handleCloseExport,
  } = useDialog()

  const {
    isExportMenuOpen,
    handleExportClick,
    handleExportMenuClose,
    handleGenerateNewFile: handleGenerateFile,
  } = useExportMenu(isFetching, refreshTasks)

  const { isGenerateDisabled } = useExportGuard({
    hasPendingTasks: pendingTasks.length > 0,
  })

  useEffect(() => {
    if (!popoverAnchorEl && isExportMenuOpen) {
      handleExportMenuClose()
    }
  }, [popoverAnchorEl, isExportMenuOpen, handleExportMenuClose])

  useEffect(() => {
    if (getStudentClass?.data.objects) {
      const initialStudentsToChecked = getStudentClass.data.objects.map(({ id }) => ({
        id,
        checked: false
      }))
      setStudentsToChecked(initialStudentsToChecked)
    }
  }, [getStudentClass?.data.objects])

  useEffect(() => {
    if (!studentsToChecked.length || !getStudentClass?.data.objects) return
    
    const checkedStudentIds = studentsToChecked.filter((el) => el.checked).map(el => el.id)
    
    if (checked) {
      const studentsWithoutJobs = getStudentClass.data.objects.filter((el) => 
        el.jobs.length === 0 && checkedStudentIds.includes(el.id)
      )
      setCheckedPerson(studentsWithoutJobs)
    } else {
      const checkedStudents = getStudentClass.data.objects.filter((el) => 
        checkedStudentIds.includes(el.id)
      )
      setCheckedPerson(checkedStudents)
    }
  }, [studentsToChecked, getStudentClass, checked])

  const handleCheckAll = (checked: boolean, studentsToCheck?: IShortClassStudent[]) => {
    if (!getStudentClass?.data.objects) return
    
    const studentsList = studentsToCheck || getStudentClass.data.objects
    const studentIds = new Set(studentsList.map(({ id }) => id))
    
    setStudentsToChecked(prev => 
      prev.map(student => 
        studentIds.has(student.id)
          ? { ...student, checked }
          : student
      )
    )
  }

  const handleCheckStudent = (studentId: number, checked: boolean) => {
    setStudentsToChecked(prev => 
      prev.map(student => 
        student.id === studentId 
          ? { ...student, checked } 
          : student
      )
    )
  }

  const { mutate: createJob, isLoading: isLoadingCreateJob } =
    useMutationPostCreateJobs({
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries("jobs")
          showSnackbar({
            title: t("jobs.succeedCreateJob"),
            variant: "success",
          })
          setRichEditorResponsibilities("")
          setRichEditorQualifications("")
          handleCloseDialogJob()
        },
        onError: (error) => {
          showSnackbar({
            title: getErrorMessageFromTab(error),
            variant: "error",
          })
        },
      },
    })

  useMutationDeleteJobs({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("jobs")
      },
      onError: (error) => {
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
          variant: "error",
        })
      },
    },
  })

  const { mutate: clearApp } = useMutationClearAllApplications({
    options: {
      onSuccess: () => {
        showSnackbar({
          title: t("jobs.allApplicationsHaveBeenRemoved"),
          variant: "success",
        })
        queryClient.invalidateQueries("jobs")
        queryClient.invalidateQueries(["jobsDetails"])
        queryClient.invalidateQueries("shortClassList")
      },
      onError: (error) => {
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
          variant: "error",
        })
      },
    },
  })

  const { mutate, isLoading } = useMutationEditClass({
    options: {
      onSuccess: () => {
        showSnackbar({
          title: "Autopay has been changed successfully",
          variant: "success",
        })
        queryClient.invalidateQueries(["classes", Number(params.classId)])
      },
      onError: (err) => {
        showSnackbar({
          title: getErrorMessageFromTab(err),
          variant: "error",
        })
      },
    },
  })

  const clearApplications = () => {
    clearApp({
      classId: Number(params.classId),
    })
    handleCloseDialogClearApplication()
  }

  const {
    isOpen: isDialogOpenJob,
    handleOpen: handleOpenDialogJob,
    handleClose: handleCloseDialogJob,
  } = useDialog()
  const {
    isOpen: isDialogOpenClearApplications,
    handleOpen: handleOpenDialogClearApplications,
    handleClose: handleCloseDialogClearApplication,
  } = useDialog()

  const handleSendAutopay = () => {
    handleClose()
    if (
      autopay.isActive === classOptions?.data.paycheck_automatic &&
      autopay.value === classOptions?.data.paycheck_interval
    )
      return

    mutate({
      classId: Number(params.classId),
      data: {
        paycheck_automatic: autopay.isActive,
        paycheck_interval: autopay.value,
      },
    })
  }

  return (
    <Stack direction="row" position="relative">
      <Stack direction="row" width="100%">
      {!isTablet && (
        <Box
          sx={{
            width: "360px",
            flexShrink: 0,
            height: "calc(100vh - 80px - 70px)",
            overflowY: "auto",
            scrollbarGutter: "stable both-edges",
          }}
        >
          <Box
            bgcolor={theme.palette.mockup.neutral100}
            border={`2px solid ${theme.palette.mockup.primary95}`}
            borderRadius={1}
            pl={2}
            pr={2}
          >
            {isLoadingStudentClass ? (
              renderMultipleComponents(
                <JobsSkeleton height="50px" style={{ margin: "10px 0" }} />,
                4
              )
            ) : !!getStudentClass?.data.objects?.length ? (
              <StudentBoard
                isRefetching={isRefetching}
                students={checked ? 
                  getStudentClass.data.objects.filter((student) => student.jobs.length === 0) : 
                  getStudentClass.data.objects
                }
                checked={checked}
                setChecked={setChecked}
                checkedPerson={checkedPerson}
                setCheckedPerson={setCheckedPerson}
                stateStudentsToChecked={{
                  studentsToChecked,
                  setStudentsToChecked,
                }}
                handleCheckAll={(checkedValue) => {
                  const displayedStudents = checked ? 
                    getStudentClass.data.objects.filter((student) => student.jobs.length === 0) : 
                    getStudentClass.data.objects
                  handleCheckAll(checkedValue, displayedStudents)
                }}
                handleCheckStudent={handleCheckStudent}
              />
            ) : (
              <NoStudent />
            )}
          </Box>
        </Box>
      )}
      <Box flex={1} ml={isTablet ? 0 : 3} minWidth={0}>
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent="space-between"
          minHeight="56px"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="left"
            gap={1}
            flexWrap="wrap"
            {...(isMobile && { mb: 2 })}
          >
            <Typography
              sx={{
                fontSize: "26px",
                fontWeight: "700",
              }}
              color="mockup.neutral10"
            >
              {t("jobs.jobBoard")}
            </Typography>
            {isLoadingClassOptions ? (
              <StudentSkeleton height="50px" />
            ) : (
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Box
                  lineHeight="16px"
                  bgcolor={
                    classOptions?.data.is_open_for_job_applications
                      ? "mockup.success90"
                      : "mockup.neutral90"
                  }
                  color={
                    classOptions?.data.is_open_for_job_applications
                      ? "mockup.success50"
                      : "mockup.neutral40"
                  }
                  p="4px 8px"
                  borderRadius="6px"
                  maxHeight="24px"
                >
                  {classOptions?.data.is_open_for_job_applications
                    ? t("jobs.open").toUpperCase()
                    : t("jobs.closed").toUpperCase()}
                </Box>
                <Box
                  display="inline"
                  lineHeight="16px"
                  bgcolor={
                    classOptions?.data.paycheck_automatic
                      ? "mockup.success90"
                      : "mockup.error90"
                  }
                  p="4px 8px"
                  borderRadius="6px"
                  maxHeight="24px"
                  color={
                    classOptions?.data.paycheck_automatic
                      ? "mockup.success50"
                      : "mockup.error50"
                  }
                >
                  {`${t("jobs.autopay").toUpperCase()}: ${
                    classOptions?.data.paycheck_automatic
                      ? t("jobs.on")
                      : t("jobs.off")
                  } `}
                </Box>
              </Stack>
            )}
          </Stack>
          <Box
            display="flex"
            justifyContent={isMobile ? "center" : "space-between"}
            alignItems="center"
          >
            <AddJobMenu
              buttonLabel={t("jobs.addJob")}
              onAddJob={() => {
                mixpanel.track("add_class_settings", {
                  action: "add_job_searchbar",
                })
                handleOpenDialogJob()
              }}
              onAddSuggestedJobs={() => {
                mixpanel.track("add_class_settings", {
                  action: "add_suggested_job",
                })
                handleOpenDialogSuggested()
              }}
              addJobLabel={t("jobs.addJob")}
              addSuggestedLabel={t("jobs.addSuggestedJobs")}
            />
            <Dialog
              titleText={t("jobs.addJob")}
              open={isDialogOpenJob}
              isLoading={isLoadingCreateJob}
              onClose={() => {
                setRichEditorResponsibilities("")
                setRichEditorQualifications("")
                handleCloseDialogJob()
                mixpanel.track("add_class_settings", {
                  action: "cancel_add_job",
                })
              }}
              desktopMaxWidth="1040px"
              tabletMaxWidth="534px"
              actionAcceptText={t("jobs.addJob")}
              actionAcceptButtonProps={{
                type: "submit",
                form: "addNewJob",
                style: { padding: isMobile ? "16px 27px" : "" },
                disabled: isLoadingCreateJob,
              }}
            >
              <AddJob
                setRichEditorResponsibilities={setRichEditorResponsibilities}
                richEditorResponsibilities={richEditorResponsibilities}
                setRichEditorQualifications={setRichEditorQualifications}
                richEditorQualifications={richEditorQualifications}
                id="addNewJob"
                createJob={createJob}
              />
            </Dialog>

            {/* New Suggested Jobs Dialog */}
            <Dialog
              titleText={t("jobs.addSuggestedJobs")}
              open={isDialogOpenSuggested}
              onClose={(event, reason) => {
                if (reason === "backdropClick") {
                  handleOpenDialogCloseSuggestedJobs()
                } else {
                  handleCloseWithConfirmation()
                }
                mixpanel.track("add_class_settings", {
                  action: "cancel_add_suggested_job",
                })
              }}
              desktopMaxWidth="1040px"
              tabletMaxWidth="867px"
              actionAcceptText={t("jobs.addSelectedJobs", {
                count: checkedSuggestedJobs.length,
              })}
              onActionButtonClick={handleCreateSuggestedJobs}
            >
              <>
                <Typography variant="body2" color="mockup.neutral10">
                  {t("jobs.clickOnTheTextToEditTheSuggestedSettings")}
                </Typography>
                <AddSuggestedJobs
                  paymentClass={classOptions?.data}
                  handleSuggestedJobs={handleSuggestedJobs}
                  setChecked={setCheckedSuggestedJobs}
                  checked={checkedSuggestedJobs}
                  errorSuggested={errorSuggested}
                  handleOpenDialogCloseSuggestedJobs={
                    handleOpenDialogCloseSuggestedJobs
                  }
                />
              </>
            </Dialog>
            <Dialog
              open={isDialogCloseSuggestedJobs}
              onClose={handleCloseDialogCloseSuggestedJobs}
              titleText={t("jobs.areYouSureYouWantToExit")}
              descriptionText={t("jobs.noChangesWillBeSaved")}
              actionAcceptText={t("jobs.accept")}
              onActionButtonClick={handleCloseWithConfirmation}
            />

            <Fab
              disabled={isLoading}
              aria-label={t("jobs.jobOptions")}
              aria-haspopup="true"
              aria-expanded={!!popoverAnchorEl}
              onClick={(e) => {
                handleClick(e)
                mixpanel.track("job_options")
              }}
              sx={{ marginLeft: "16px", minWidth: "48px" }}
            >
              <IconSettings aria-hidden="true" />
            </Fab>
            <PopOverStyle
              open={!!popoverAnchorEl}
              onClose={handleSendAutopay}
              anchorEl={popoverAnchorEl}
              marginThreshold={40}
              anchorOrigin={{
                vertical: 60,
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <Content
                onSubmitAutopay={handleSendAutopay}
                getAutopayValues={(isActive, value) =>
                  setAutopay({
                    isActive,
                    value,
                  })
                }
                isFetchingExportClass={isFetching}
                onExportData={(event: MouseEvent<HTMLDivElement>) => {
                  if (isExportMenuOpen) {
                    handleExportMenuClose()
                  } else {
                    handleExportClick(event)
                  }
                }}
                onJobsOpen={() => {
                  patchOpenJob({
                    classId: Number(params.classId),
                    data: {
                      is_open_for_job_applications:
                        !classOptions?.data.is_open_for_job_applications,
                    },
                  })
                }}
                handleOpenDialog={handleOpenDialogClearApplications}
                isOpenApplication={
                  classOptions?.data.is_open_for_job_applications
                }
                paycheck={{
                  isActive: classOptions?.data.paycheck_automatic,
                  interval: classOptions?.data.paycheck_interval,
                }}
                successTasks={successTasks}
                onGenerateNewFile={() => {
                  handleGenerateFile(() =>
                    schoolPlanToExportData({
                      user: userInfo,
                      upgrade: handleOpenExport,
                      exportData: refetchExportData,
                    })
                  )
                  handleExportMenuClose()
                }}
                onFileClick={(taskId) => {
                  setExportReportIdToDownload(taskId)
                  handleExportMenuClose()
                }}
                isExportMenuOpen={isExportMenuOpen}
                isGenerateDisabled={isGenerateDisabled}
                generateDisabledTooltip={t("export.reportCurrentlyBeingGenerated")}
              />
            </PopOverStyle>
            <DialogUpgradeAccount
              open={isOpenExport}
              onClose={() => {
                handleCloseExport()
                mixpanel.track("reports_export", {
                  method: "class_jobs",
                  action: "cancel",
                })
              }}
              onActionButtonClick={() => {
                payIt(null)
                mixpanel.track("reports_export", {
                  method: "class_jobs",
                  action: "submit",
                })
              }}
              isLoading={isLoadingPayments}
              isExport
            />
            <DialogClearApplication
              open={isDialogOpenClearApplications}
              onClose={handleCloseDialogClearApplication}
              onActionButtonClick={clearApplications}
              descriptionText={t(
                "jobs.thisWillClearAllHiredStudentsAndPendingApplicationsFromYourJobBoard"
              )}
              descriptionTextProps={{
                variant: "body1",
                fontWeight: "500",
                color: "mockup.neutral10",
              }}
            />
          </Box>
        </Stack>
        {loadingGetJobs ? (
          renderMultipleComponents(
            <JobsSkeleton
              height="200px"
              style={{
                margin: "10px 0 ",
              }}
            />,
            2
          )
        ) : (
          <Stack
            direction={getJobs && getJobs.data.length > 0 ? "row" : "column"}
            flexWrap="wrap"
            justifyContent="flex-start"
            mt={2}
            sx={{
              maxHeight: "calc(100vh - 65px - 80px - 85px)",
              overflowY: "auto",
            }}
          >
            {getJobs && getJobs.data.length > 0 ? (
              <JobsOffer
                isRefetching={isRefetching}
                richEditorResponsibilities={richEditorResponsibilities}
                setRichEditorResponsibilities={setRichEditorResponsibilities}
                setRichEditorQualifications={setRichEditorQualifications}
                richEditorQualifications={richEditorQualifications}
                data={getJobs.data}
                students={getStudentClass?.data.objects || []}
                checkedPerson={checkedPerson}
                setStudentsToChecked={setStudentsToChecked}
                setCheckedPerson={setCheckedPerson}
              />
            ) : (
              <NoJobs
                richEditorResponsibilities={richEditorResponsibilities}
                setRichEditorResponsibilities={setRichEditorResponsibilities}
                richEditorQualifications={richEditorQualifications}
                setRichEditorQualifications={setRichEditorQualifications}
                onAddJob={() => {
                  mixpanel.track("add_class_settings", {
                    action: "add_job_searchbar",
                  })
                  handleOpenDialogJob()
                }}
                onAddSuggestedJobs={() => {
                  mixpanel.track("add_class_settings", {
                    action: "add_suggested_job",
                  })
                  handleOpenDialogSuggested()
                }}
              />
            )}
          </Stack>
        )}
      </Box>
      </Stack>
      <ExportPendingBanner visible={pendingTasks.length > 0} />
    </Stack>
  )
}

export default Jobs
